"use client"

import { Play, Pause, Repeat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { playToggleA11y } from '@/lib/a11y/play-toggle'

interface AudioControlsProps {
  isPlaying: boolean
  isLoopEnabled: boolean
  previewMode: 'original' | 'processed'
  hasProcessedAudio: boolean
  onPlayPause: () => void
  onLoopToggle: () => void
  onPreviewModeChange: (mode: 'original' | 'processed') => void
  disabled?: boolean
  className?: string
}

export function AudioControls({
  isPlaying,
  isLoopEnabled,
  previewMode,
  hasProcessedAudio,
  onPlayPause,
  onLoopToggle,
  onPreviewModeChange,
  disabled = false,
  className,
}: AudioControlsProps) {
  const neutralToggleClass =
    "border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] text-[var(--tls-fg-muted)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
  const activeToggleClass =
    "border-[color:color-mix(in_srgb,var(--tls-brand)_24%,transparent)] bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-brand)] hover:border-[color:color-mix(in_srgb,var(--tls-brand)_32%,transparent)] hover:bg-[color:color-mix(in_srgb,var(--tls-brand)_14%,var(--tls-surface))] hover:text-[var(--tls-brand)]"

  return (
    <div className={cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", className)}>
      {/* Playback controls */}
      <div className="flex items-center gap-2">
        {/* Play/Pause button */}
        <Button
          onClick={onPlayPause}
          disabled={disabled}
          size="icon"
          className="size-12 rounded-[var(--tls-radius-pill)]"
          // Playback state is exposed via aria-pressed with a stable name (TES-1581).
          {...playToggleA11y({ playing: isPlaying })}
        >
          {isPlaying ? (
            <Pause className="size-5" />
          ) : (
            <Play className="size-5 ml-0.5" />
          )}
        </Button>

        {/* Loop toggle */}
        <Button
          onClick={onLoopToggle}
          disabled={disabled}
          variant="outline"
          className={cn(
            "min-h-[44px] gap-1.5 px-3 py-2 text-sm",
            isLoopEnabled
              ? activeToggleClass
              : neutralToggleClass
          )}
          aria-label={isLoopEnabled ? "Disable loop" : "Enable loop"}
        >
          <Repeat className="size-4" />
          <span className="hidden sm:inline">
            {isLoopEnabled ? "Loop On" : "Loop"}
          </span>
        </Button>
      </div>

      {/* Preview mode toggle (original vs processed) */}
      {hasProcessedAudio && (
        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-[var(--tls-fg-subtle)] sm:inline">Preview:</span>
          <div
            role="group"
            aria-label="Preview mode"
            className="flex items-center rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] p-1"
          >
            <button type="button"
              onClick={() => onPreviewModeChange('original')}
              aria-pressed={previewMode === 'original'}
              className={cn(
                "min-h-[44px] rounded-[var(--ls-radius-0)] px-3 py-1.5 text-sm transition-[background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                previewMode === 'original'
                  ? "bg-[var(--tls-surface-hover)] text-[var(--tls-fg-strong)]"
                  : "text-[var(--tls-fg-muted)] hover:text-[var(--tls-fg-strong)]"
              )}
            >
              Original
            </button>
            <button type="button"
              onClick={() => onPreviewModeChange('processed')}
              aria-pressed={previewMode === 'processed'}
              className={cn(
                "min-h-[44px] rounded-[var(--ls-radius-0)] px-3 py-1.5 text-sm transition-[background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                previewMode === 'processed'
                  ? "bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-brand)]"
                  : "text-[var(--tls-fg-muted)] hover:text-[var(--tls-fg-strong)]"
              )}
            >
              Processed
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
